// @intl project:microsite

import React from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import classnames from 'classnames'
import { useTheme } from 'styled-components/native'

import {
  ONBOARDING_WELLNESS_GOALS_PATH,
  PAGE_ROUTES,
  WELLNESS_TOOLKIT_BASE_ROUTE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { getLegalNoticeLink } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getLegalNoticeLink'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

import { CoverageNotice } from './CoverageNotice'
import styles from './footer.module.scss'
import { getIsCustomerCareTeam, getLegalNotice } from '../../../data/customer/customerSelectors'
import { getIsUserInternational, getUser } from '../../../data/user/userSelectors'
import {
  isPathInHealthChallengesFlow,
  isPathInNeurodiversityFlow,
  isPathInWomensHealthFlow,
} from '../../../features/centerOfExcellence/centerOfExcellenceUtils'
import { CMS_MICROSITE_ROUTES } from '../../../features/onboard/data/page-navigation/location-actions'
import { isPathInAutoEnrollmentFlow } from '../../../features/register/data/registerUtils'
import { AUTO_ENROLLMENT_PATHWAYS_PATH, REGISTRATION_ONBOARDING } from '../../constants/registrationFormConstants'
import { useProviderRequestPage } from '../../hooks/useProviderRequestPage'
import useUpdatedSchedulingPage from '../../hooks/useUpdatedSchedulingPage'
import { hasRole } from '../../utils/userUtils'
import { CoverageTermsContent } from '../modals/legalNotices/CoverageTermsContent'

type Props = {
  isUserSignedIn: boolean
  legalNotice: React.ReactNode
  isUserInternational: boolean
  shouldUnsetBackgroundStyle?: boolean
  isCareTeamPage?: boolean
}

const LegalFooter = ({
  isUserSignedIn,
  legalNotice,
  isUserInternational,
  shouldUnsetBackgroundStyle,
  isCareTeamPage,
}: Props) => {
  const { activeLanguage } = useLyraIntl()
  const { pathname } = useLocation()
  const { isDarkMode, colors, isAuroraTheme } = useTheme() as ThemeType
  const isOnUpdatedBookingPage = useUpdatedSchedulingPage(pathname)
  const isProviderRequestPage = useProviderRequestPage(pathname)
  const isOnCmsMicrosite = CMS_MICROSITE_ROUTES.some((path) => pathname.startsWith(path))
  const isPathInWellnessCheckIn = pathname.startsWith('/secure/wellness-check-in/results')
  const isPathInTraumaCheckInResults = pathname.startsWith('/secure/wellness/trauma-checkin/results')
  const isPathAutoEnrollmentPathway = pathname.startsWith(AUTO_ENROLLMENT_PATHWAYS_PATH)
  const isSignedInBackgroundInfoPage = pathname.startsWith(REGISTRATION_ONBOARDING)
  const isOnboardingWellnessGoalsPage = pathname.includes(ONBOARDING_WELLNESS_GOALS_PATH)
  const isNeurodiversityPage = isPathInNeurodiversityFlow(pathname)
  const isHealthChallengesPage = isPathInHealthChallengesFlow(pathname)
  const isWomensHealthPage = isPathInWomensHealthFlow(pathname)
  const isZeroSearchResultsRecommendedTreatmentOptionPage = pathname.startsWith(
    PAGE_ROUTES.ZERO_SEARCH_RESULTS_RECOMMENDED_TREATMENT_OPTION,
  )

  const shouldHideFooter =
    isProviderRequestPage ||
    isPathInAutoEnrollmentFlow(pathname) ||
    isPathInWellnessCheckIn ||
    isPathInTraumaCheckInResults ||
    isPathAutoEnrollmentPathway ||
    isOnboardingWellnessGoalsPage

  const isOatmealBackground =
    !isAuroraTheme &&
    ((pathname.includes(WELLNESS_TOOLKIT_BASE_ROUTE) && !isDarkMode) ||
      isNeurodiversityPage ||
      isHealthChallengesPage ||
      isWomensHealthPage ||
      isZeroSearchResultsRecommendedTreatmentOptionPage)

  const isToolkitDirectoryPage = pathname.includes(PAGE_ROUTES.TOOLKIT_DIRECTORY)

  const getToolkitDirectoryFooterStyle = () => {
    if (!isToolkitDirectoryPage) return undefined
    if (!isAuroraTheme) return styles.backgroundElement
    return isDarkMode ? styles.auroraDarkBackgroundPrimary : styles.auroraLightBackgroundPrimary
  }

  if (shouldHideFooter) {
    return null
  }

  return (
    <div
      className={classnames(styles.footer, {
        [styles.bookingPage]: isOnUpdatedBookingPage,
        [styles.unsetBackgroundStyle]: shouldUnsetBackgroundStyle,
        [styles.cmsMicrosite]: isOnCmsMicrosite,
        [styles.oatmeal]: isOatmealBackground,
        [getToolkitDirectoryFooterStyle() ?? '']: isToolkitDirectoryPage,
      })}
      data-test-id='legal-footer'
    >
      <div
        className={classnames(styles.inner, {
          [styles.carePage]: isCareTeamPage,
          [styles.cmsMicrositeInner]: isOnCmsMicrosite,
        })}
      >
        {isUserSignedIn && !isSignedInBackgroundInfoPage ? (
          []
        ) : (
          /* eslint-disable formatjs/no-literal-string-in-jsx */
          <div className={classnames(styles['copyright-contact'])} style={{ color: colors.textSecondary }}>
            &copy;{new Date().getFullYear()}&nbsp;Lyra&nbsp;Health,&nbsp;INC
            <i>&nbsp;&bull;&nbsp;</i>
            270 EAST LN, BURLINGAME, CA 94010
          </div>
          /* eslint-enable formatjs/no-literal-string-in-jsx */
        )}
        <ul
          className={classnames(styles['footer-links'])}
          style={{ color: isAuroraTheme && isDarkMode ? colors.linkDefault : colors.textSecondary }}
        >
          <li>
            <CoverageTermsContent legalLanguage={isUserSignedIn ? legalNotice : <CoverageNotice signedOut={true} />} />
          </li>
          {!isUserInternational && (
            <li>
              <a href={getLegalNoticeLink(activeLanguage, 'HIPAA_NOTICE')} target='_blank' rel='noreferrer'>
                <FormattedMessage
                  defaultMessage='HIPAA Notice'
                  description='A short link to the HIPAA Notice, shown in page footer'
                />
              </a>
            </li>
          )}
          <li>
            <a href={getLegalNoticeLink(activeLanguage, 'PRIVACY_POLICY')} target='_blank' rel='noreferrer'>
              <FormattedMessage
                defaultMessage='Privacy Policy'
                description='A short link to the Privacy Policy, shown in page footer'
              />
            </a>
          </li>
          <li>
            <a href={getLegalNoticeLink(activeLanguage, 'TERMS_OF_USE')} target='_blank' rel='noreferrer'>
              <FormattedMessage
                defaultMessage='Terms of Use'
                description='A short link to the Terms of Use, shown in page footer'
              />
            </a>
          </li>
          <li>
            <a href='https://www.lyrahealth.com/accessibility/' target='_blank' rel='noreferrer'>
              <FormattedMessage
                defaultMessage='Accessibility'
                description='A short link to Accessibility info, shown in page footer'
              />
            </a>
          </li>
        </ul>
      </div>
    </div>
  )
}

const mapStateToProps = (state: $TSFixMe) => {
  return {
    isUserSignedIn: hasRole(getUser(state), 'ROLE_usr'),
    legalNotice: getLegalNotice(state),
    isUserInternational: getIsUserInternational(state),
    isCareTeamPage: getIsCustomerCareTeam(state),
  }
}

export default connect(mapStateToProps)(LegalFooter)
